import React, { useState } from 'react';
import { Button, Typography, Card, Space, Result, Timeline, Alert, Grid } from 'antd';
import { 
    CheckCircleOutlined, 
    LoginOutlined,
    MailOutlined,
    PhoneOutlined,
    FileTextOutlined,
    UserOutlined,
    FileProtectOutlined,
    DownloadOutlined
} from '@ant-design/icons';
import { useSelfRegistration } from '../../hooks/useSelfRegistration';
import { showSuccessNotification, showErrorNotification } from '@/components/common/utils/notify';
import { SelfRegistrationSessionDTO } from '@/shared/api/model';
import { getCompanyDisplayName, useCompany } from '@/hooks/company/use-company';

const { Title, Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

interface RegistrationCompleteStepProps {
    session: SelfRegistrationSessionDTO;
    onComplete: () => void;
    onError: (message: string) => void;
}

const RegistrationCompleteStep: React.FC<RegistrationCompleteStepProps> = ({
    session,
    onComplete,
    onError
}) => {
    const screens = useBreakpoint();
    const isMobile = !screens.md;
    const { data: companyData } = useCompany();
    const institutionName = getCompanyDisplayName(companyData);

    const hasDeductionFormPending = !!(
        session.uploadedDocuments?.some(
            (d: any) => d.documentType === 'OFFICIAL_DEDUCTION_FORM'
        ) && !session.signedDeductionFormUploaded
    );
    
    // Use the refactored self registration hook
    const {
        completeRegistration,
        isCompletingRegistration
    } = useSelfRegistration();

    const handleCompleteRegistration = async () => {
        try {
            if (!session.sessionToken) {
                throw new Error('Session token is required');
            }
            
            // Call the actual API using the refactored hook
            await completeRegistration(session.sessionToken);
            showSuccessNotification('Registration completed successfully');
            onComplete();
            
        } catch (error: any) {
            onError(error.message || 'Failed to complete registration');
        }
    };

    return (
        <div>
            <Result
                icon={<CheckCircleOutlined style={{ 
                    color: '#52c41a',
                    fontSize: isMobile ? '48px' : '64px'
                }} />}
                title={<Title level={isMobile ? 3 : 2} style={{ color: '#52c41a' }}>Registration Successful!</Title>}
                subTitle={
                    <Text style={{ 
                        fontSize: isMobile ? '14px' : '16px',
                        display: 'block'
                    }}>
                        Congratulations! Your {institutionName} registration has been completed successfully.
                    </Text>
                }
                extra={[
                    <Button
                        key="login"
                        type="primary"
                        size={isMobile ? 'middle' : 'large'}
                        icon={<LoginOutlined />}
                        onClick={handleCompleteRegistration}
                        loading={isCompletingRegistration}
                        style={{ 
                            height: isMobile ? '44px' : '48px',
                            fontSize: isMobile ? '16px' : '18px'
                        }}
                    >
                        Go to Login
                    </Button>
                ]}
            />

            {/* Registration Summary */}
            <Card 
                title="Registration Summary" 
                style={{ marginBottom: isMobile ? '16px' : '20px' }}
                size={isMobile ? 'small' : 'default'}
            >
                <Timeline>
                    <Timeline.Item color="green" dot={<PhoneOutlined />}>
                        <Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>Phone Verified</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: isMobile ? '12px' : '14px' }}>{session.phoneNumber}</Text>
                    </Timeline.Item>
                    
                    <Timeline.Item color="green" dot={<FileTextOutlined />}>
                        <Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>Terms Accepted</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: isMobile ? '12px' : '14px' }}>Terms and conditions accepted</Text>
                    </Timeline.Item>
                    
                    <Timeline.Item color="green" dot={<MailOutlined />}>
                        <Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>Email Verified</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: isMobile ? '12px' : '14px' }}>{session.email}</Text>
                    </Timeline.Item>
                    
                    <Timeline.Item color="green" dot={<UserOutlined />}>
                        <Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>Personal Information</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: isMobile ? '12px' : '14px' }}>Profile completed</Text>
                    </Timeline.Item>
                    
                    <Timeline.Item color="green" dot={<FileTextOutlined />}>
                        <Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>Documents Uploaded</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: isMobile ? '12px' : '14px' }}>All required documents submitted</Text>
                    </Timeline.Item>
                    
                    {session.requiresOfficerApproval && (
                        <Timeline.Item color="green" dot={<CheckCircleOutlined />}>
                            <Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>Officer Approved</Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: isMobile ? '12px' : '14px' }}>Application approved by officer</Text>
                        </Timeline.Item>
                    )}
                    
                    <Timeline.Item color="green" dot={<CheckCircleOutlined />}>
                        <Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>Registration Complete</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: isMobile ? '12px' : '14px' }}>Account created successfully</Text>
                    </Timeline.Item>
                </Timeline>
            </Card>

            {/* Account Information */}
            <Card 
                title="Your Account Details" 
                style={{ marginBottom: isMobile ? '16px' : '20px' }}
                size={isMobile ? 'small' : 'default'}
            >
                <Space direction="vertical" style={{ width: '100%' }}>
                    <div>
                        <Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>Full Name:</Text>
                        <br />
                        <Text style={{ fontSize: isMobile ? '13px' : '14px' }}>{session.fullName}</Text>
                    </div>
                    <div>
                        <Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>Phone Number:</Text>
                        <br />
                        <Text style={{ fontSize: isMobile ? '13px' : '14px' }}>{session.phoneNumber}</Text>
                    </div>
                    <div>
                        <Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>Email Address:</Text>
                        <br />
                        <Text style={{ fontSize: isMobile ? '13px' : '14px' }}>{session.email}</Text>
                    </div>
                    <div>
                        <Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>Registration Date:</Text>
                        <br />
                        <Text style={{ fontSize: isMobile ? '13px' : '14px' }}>{session.createdAt ? new Date(session.createdAt).toLocaleDateString() : 'Unknown'}</Text>
                    </div>
                    <div>
                        <Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>Reference Number:</Text>
                        <br />
                        <Text code style={{ fontSize: isMobile ? '12px' : '14px' }}>{session.sessionToken ? session.sessionToken.substring(0, 12).toUpperCase() : 'N/A'}</Text>
                    </div>
                </Space>
            </Card>

            {/* Deduction Form Notice — shown when officer has uploaded an official form */}
            {hasDeductionFormPending && (
                <Alert
                    icon={<FileProtectOutlined />}
                    message="Action Required: Deduction Form"
                    description={
                        <div>
                            <Paragraph style={{ fontSize: isMobile ? '13px' : '14px', marginBottom: 8 }}>
                                An officer has prepared an <Text strong>Official Deduction Form</Text> for your account.
                                After logging in, you must:
                            </Paragraph>
                            <Paragraph style={{ fontSize: isMobile ? '13px' : '14px', marginBottom: 4 }}>
                                <DownloadOutlined /> <Text strong>1. Download</Text> the official deduction form from your profile page.
                            </Paragraph>
                            <Paragraph style={{ fontSize: isMobile ? '13px' : '14px', marginBottom: 4 }}>
                                <FileProtectOutlined /> <Text strong>2. Sign</Text> the form.
                            </Paragraph>
                            <Paragraph style={{ fontSize: isMobile ? '13px' : '14px', marginBottom: 0 }}>
                                <FileTextOutlined /> <Text strong>3. Upload</Text> the signed form via your profile page at <Text strong>Profile → Deduction Information</Text>.
                            </Paragraph>
                        </div>
                    }
                    type="warning"
                    showIcon
                    style={{ marginBottom: isMobile ? '16px' : '20px' }}
                />
            )}

            {/* Next Steps */}
            <Alert
                message="What's Next?"
                description={
                    <div>
                        <Paragraph style={{ fontSize: isMobile ? '13px' : '14px' }}>
                            <Text strong>1. Login to Your Account</Text>
                            <br />
                            Use your phone number and the temporary password sent via SMS/email to login at{' '}
                            <Text strong>{window.location.origin}/login</Text>.
                        </Paragraph>
                        {hasDeductionFormPending && (
                            <Paragraph style={{ fontSize: isMobile ? '13px' : '14px' }}>
                                <Text strong>2. Complete Deduction Form</Text>
                                <br />
                                After logging in, go to <Text strong>Profile → Deduction Information</Text> to download, sign, and upload your signed deduction form.
                            </Paragraph>
                        )}
                        <Paragraph style={{ fontSize: isMobile ? '13px' : '14px' }}>
                            <Text strong>{hasDeductionFormPending ? '3' : '2'}. Complete Your Profile</Text>
                            <br />
                            Add additional information and preferences to personalize your experience.
                        </Paragraph>
                        <Paragraph style={{ 
                            marginBottom: 0,
                            fontSize: isMobile ? '13px' : '14px'
                        }}>
                            <Text strong>{hasDeductionFormPending ? '4' : '3'}. Explore Services</Text>
                            <br />
                            Discover savings accounts, loans, and other financial services offered by {institutionName}.
                        </Paragraph>
                    </div>
                }
                type="success"
                showIcon
                // @ts-ignore
                size={isMobile ? 'small' : 'default'}
                style={{ marginBottom: isMobile ? '16px' : '20px' }}
            />

            {/* Important Information */}
            <Card 
                style={{ backgroundColor: '#f6ffed' }}
                size={isMobile ? 'small' : 'default'}
            >
                <Title
                    // @ts-ignore
                    level={isMobile ? 6 : 5} style={{ color: '#52c41a' }}>Important Information</Title>
                <Paragraph style={{ fontSize: isMobile ? '13px' : '14px' }}>
                    <Text strong>Login Credentials:</Text> You will receive your temporary password via SMS within the next few minutes. 
                    Please change this password when you first login for security purposes.
                </Paragraph>
                <Paragraph style={{ fontSize: isMobile ? '13px' : '14px' }}>
                    <Text strong>Account Activation:</Text> Your account is now active and ready to use. 
                    You can access {institutionName} services after logging in.
                </Paragraph>
                <Paragraph style={{ 
                    marginBottom: 0,
                    fontSize: isMobile ? '13px' : '14px'
                }}>
                    <Text strong>Support:</Text> If you need any assistance, contact our support team or call our helpline.
                </Paragraph>
            </Card>

            {/* Footer Actions */}
            <div style={{ textAlign: 'center', marginTop: isMobile ? '20px' : '30px' }}>
                <Space>
                    <Button
                        type="primary"
                        size={isMobile ? 'middle' : 'large'}
                        icon={<LoginOutlined />}
                        onClick={handleCompleteRegistration}
                        loading={isCompletingRegistration}
                        style={{ 
                            height: isMobile ? '44px' : '48px',
                            fontSize: isMobile ? '16px' : '18px'
                        }}
                    >
                        Continue to Login
                    </Button>
                </Space>
                
                <div style={{ marginTop: isMobile ? '16px' : '20px' }}>
                    <Text type="secondary" style={{ fontSize: isMobile ? '13px' : '14px' }}>
                        Thank you for choosing {institutionName}. Welcome to our community!
                    </Text>
                </div>
            </div>
        </div>
    );
};

export default RegistrationCompleteStep;
